import { PlayersToolbar } from "@/components/organisms/PlayersToolbar/PlayersToolbar";
import { canonicalUrl } from "@/config/canonical";
import { GAME_NAME } from "@/config/game";
import { HeroSection } from "@/containers/HeroSection";
import {
  trackAddedPlayerToEvolutionBuilder,
  trackFilter,
  trackQuickFilter,
} from "@/events/evolutionsMixpanel";
import useDebounce from "@/hooks/useDebounce";
import { useGoBack } from "@/hooks/useGoBack";
import {
  quickFilterKeyFromType,
  useMyClubFilter,
} from "@/hooks/useMyClubFilter";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { useSticky } from "@/hooks/useSticky";
import {
  PlayersViewToggle,
  usePlayersView,
} from "@/pages/Players/PlayersViewToggle";
import { QuickFilters } from "@/pages/Players/QuickFilters";
import {
  addPlayerToEvolutionBuilder,
  getCurrentEvolutionPlayerSelector,
} from "@/redux/evolutions/evolutionsBuilderSlice";
import {
  MetaRatingsPlayer,
  PlayerRole,
  clearPlayers,
  fetchPlayers,
  ownedOrEvolvedSelector,
  playerFiltersAsQueryParamsSelector,
  playersSelector,
  updateFilter,
} from "@/redux/players/playersSlice";
import { AppDispatch } from "@/redux/store";
import { BREAKPOINTS } from "@/settings";
import type { PlayersSortByType } from "@repo/shared/api";
import { useTranslation } from "@repo/shared/i18n";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useParams } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import { EvolutionsHubCards } from "../EvolutionsHub";
import { EvolutionsMobileNav } from "../EvolutionsMobileNav";
import { ToolbarAction } from "../EvolutionsPage/EvolutionsPage";
import { EvolutionsPlayersSection } from "../sections/EvolutionsPlayersSection";

export const EvolutionBuilderPlayerPicker = () => {
  const { t } = useTranslation(["evoBuilder", "common"]);
  const { resourceId } = useParams();
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const [scrollPastTopBar, refTopBar] = useSticky({
    triggerOn: "bottom",
    offset: isTouchDevice ? 80 : 200,
  }) as [boolean, React.MutableRefObject<HTMLDivElement>];
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);

  // Player selection state
  const [playerRole, setPlayerRole] = useState<PlayerRole>();
  const [searchTerm, setSearchTerm] = useState("");
  const { debouncedSearchTerm, errorMessage: searchTermErrorMessage } =
    useDebounce(searchTerm, {
      minLength: 3,
      contextForErrorMessage: t("common:playerName"),
    });
  const [elementOpened, setElementOpened] = useState<ToolbarAction>("none");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<PlayersSortByType>("evo-popularity");
  const sortBeforeSearchRef = useRef<PlayersSortByType | null>(null);

  // Refs for outside click handling
  const refPlayerRole = useOutsideClick(() => {
    if (elementOpened === "playerRole") setElementOpened("none");
  });
  const refFilters = useOutsideClick(() => {
    if (elementOpened === "filters") setElementOpened("none");
  });

  // Selectors
  const ownedOrEvolvedFilters = useSelector(ownedOrEvolvedSelector);
  const currentEvolutionPlayer = useSelector(getCurrentEvolutionPlayerSelector);
  const stableQueryParams = useSelector(playerFiltersAsQueryParamsSelector);

  const {
    players,
    totalPages,
    playersStatus,
    playerFilters,
    playerRoles,
    requestedFromPage,
  } = useSelector(playersSelector);

  const dispatch = useDispatch<AppDispatch>();
  const goBack = useGoBack();

  // hasFilters indicator for player selection phase
  const hasFilters = useMemo(
    () => stableQueryParams.length > 0,
    [stableQueryParams],
  );

  // Fetch players
  useEffect(() => {
    // Skip fetch if we're about to switch sort due to starting search
    if (
      debouncedSearchTerm &&
      sort === "evo-popularity" &&
      sortBeforeSearchRef.current === null
    ) {
      return;
    }

    // Skip fetch if we're about to restore sort after search cleared
    if (!debouncedSearchTerm && sortBeforeSearchRef.current !== null) {
      return;
    }

    dispatch(
      fetchPlayers({
        page,
        sort: sort,
        myClub: ownedOrEvolvedFilters?.category === "Owned",
        myEvolvedPlayers: ownedOrEvolvedFilters?.category === "Evolved",
        activeSquad: ownedOrEvolvedFilters?.category === "ActiveSquad",
        playerRoleId: playerRole?.id,
        queryParams: stableQueryParams,
        searchText: debouncedSearchTerm,
        requestedFromPage: "evolutions-builder",
      }),
    );
  }, [
    dispatch,
    page,
    playerRole,
    ownedOrEvolvedFilters,
    debouncedSearchTerm,
    stableQueryParams,
    sort,
  ]);

  // When searching, temporarily switch to meta-rating sort (only from popularity), then restore when done
  useEffect(() => {
    if (debouncedSearchTerm) {
      // Starting a search - only switch if currently on popularity
      if (sortBeforeSearchRef.current === null && sort === "evo-popularity") {
        sortBeforeSearchRef.current = sort;
        setSort("meta-rating");
      }
    } else {
      // Search cleared - restore previous sort if we had switched
      if (sortBeforeSearchRef.current !== null) {
        setSort(sortBeforeSearchRef.current);
        sortBeforeSearchRef.current = null;
      }
    }
  }, [debouncedSearchTerm, sort]);

  // Clear players on mount if coming from different page
  useEffect(() => {
    // if (!isTouchDevice) {
    //   window.scrollTo({ top: 0 });
    // }
    if (requestedFromPage !== "evolutions-builder") {
      dispatch(clearPlayers());
    }
  }, []);

  // Handlers
  const handleToolbarAction = (action: ToolbarAction) => {
    setElementOpened(elementOpened === action ? "none" : action);
  };

  const handleSelectPlayerRole = (role: PlayerRole) => {
    setPage(1);
    setPlayerRole(role);
  };

  const handleSearch = (term: string) => {
    if (sort === "date-added" && term.length > 2) {
      setPage(1);
      setSort("meta-rating");
    }
    setSearchTerm(term);
  };

  const [view, setView] = usePlayersView();
  const handleMyClubClicked = useMyClubFilter(
    (f) => {
      dispatch(updateFilter(f));
    },
    {
      onFilterApplied: (type) =>
        trackQuickFilter({
          subsection: "Builder Overview",
          filters: quickFilterKeyFromType(type),
        }),
      origin: { section: "Evolutions", subsection: "Builder Overview" },
      upgradeTracking: {
        section: "Evolutions",
        subsection: "Builder Overview",
        context: "filter",
      },
    },
  );

  const handleChooseEvolutionBuilderPlayer = (player: MetaRatingsPlayer) => {
    dispatch(addPlayerToEvolutionBuilder({ player }));
    trackAddedPlayerToEvolutionBuilder({ subsection: "Builder Overview" });
    setSearchTerm("");
  };

  const onFiltersUpdated = () => {
    const filters = stableQueryParams
      .map((qp) => Object.keys(qp ?? {})[0])
      .filter(Boolean);
    trackFilter({
      subsection: "Builder Overview",
      filters,
    });
  };

  if (resourceId) {
    return <Outlet context={currentEvolutionPlayer} />;
  }

  return (
    <main className="min-h-screen mx-auto bg-background">
      <HeroSection
        helmetTitle={t("hero.title", { game: GAME_NAME })}
        pageTitle={t("hero.title", { game: GAME_NAME })}
        helmetDescription={t("hero.description", { game: GAME_NAME })}
        subtitle={t("hero.subtitle")}
        helmetCanonicalLink={canonicalUrl("/evolutions/builder")}
        back={goBack}
        shouldBeInTheDocumentFlow
      >
        <div className="my-8 hidden mobile:block">
          <EvolutionsHubCards activeEvoCard="builder" />
        </div>
      </HeroSection>
      <EvolutionsMobileNav active="builder" />
      <div ref={refTopBar} />
      <PlayersToolbar
        searchTerm={searchTerm}
        setSearchTerm={handleSearch}
        handleToolbarAction={handleToolbarAction}
        isMobile={isMobile}
        hasFilters={hasFilters}
        sortBy={sort}
        setSortBy={setSort}
        playerRole={playerRole}
        pageType="players"
        isEvolutionsBuilderContext={true}
        viewToggle={<PlayersViewToggle value={view} onChange={setView} />}
        quickFilters={
          <QuickFilters
            onMyClubClicked={handleMyClubClicked}
            onFiltersChanged={() => {}}
          />
        }
      />

      <div className="max-w-content mx-auto">
        <EvolutionsPlayersSection
          onFiltersUpdated={onFiltersUpdated}
          elementOpened={elementOpened}
          playersStatus={playersStatus}
          players={players}
          isTouchDevice={isTouchDevice}
          selectedPlayerRoles={playerRole ? [playerRole] : []}
          playerFilters={playerFilters}
          scrollPastTopBar={scrollPastTopBar}
          handleToolbarAction={handleToolbarAction}
          hasFilters={hasFilters}
          playerRoles={playerRoles}
          handleSelectPlayerRole={handleSelectPlayerRole}
          multiSelectPlayerRoles={false}
          refPlayerRole={refPlayerRole}
          refFilters={refFilters}
          handleMyClubClicked={handleMyClubClicked}
          page={page}
          totalPages={totalPages}
          setPage={setPage}
          sortBy="meta-rating"
          view={view}
          basePath="/evolutions/builder"
          onSelectPlayer={handleChooseEvolutionBuilderPlayer}
          showEvoPopularity
          builderContext="evolution-builder"
          searchTermErrorMessage={searchTermErrorMessage}
        />
      </div>
    </main>
  );
};
